(() => {
  const fail = message => { throw new Error(`TMX configuration: ${message}`); };
  const id = value => typeof value === 'string' && /^[1-9]\d*$/.test(value);
  const unique = values => new Set(values).size === values.length;
  const modes = ['water', 'water_angled', 'plug', 'hidden', 'hybrid'];
  const roles = ['water_valves', 'heater', 'hybrid_valves', 'accessory'];
  const validPower = power => Number.isInteger(power) && power > 0 && power <= 3000;
  function inheritPowers(product, options, finish) {
    if (!finish || product.variantCount !== product.variants.length) return {};
    const groups = new Map();
    const keys = options.map(option => {
      const values = new Map();
      for (const value of option.values) values.set(value.name, values.has(value.name) ? null : value.id);
      return values;
    });
    for (const variant of product.variants) {
      if (!Array.isArray(variant.options) || variant.options.length !== options.length) continue;
      const values = options.flatMap((option, index) => option === finish ? [] : [keys[index].get(variant.options[index])]);
      if (values.some(value => !id(value))) continue;
      const key = JSON.stringify(values);
      if (!groups.has(key)) groups.set(key, {powers: new Set(), missing: [], invalid: false});
      const group = groups.get(key), power = variant.technical?.power;
      if (power == null) group.missing.push(variant.id);
      else if (validPower(power)) group.powers.add(power);
      else group.invalid = true;
    }
    return Object.fromEntries([...groups.values()].flatMap(group =>
      !group.invalid && group.powers.size === 1 ? group.missing.map(variantId => [variantId, [...group.powers][0]]) : []
    ));
  }
  function resolve(data, legacy) {
    const config = data.configuration;
    if (config == null) return legacy;
    if (config.version !== 1) fail('unsupported version');
    const product = data.product;
    if (!id(product.id)) fail('invalid owner');
    if (!Array.isArray(config.modes) || !unique(config.modes) || config.modes.some(mode => !modes.includes(mode))) fail('invalid modes');
    if (!['none', 'print', 'slab'].includes(config.personalization)) fail('invalid personalization');
    if (!Array.isArray(config.accessoryIds) || !unique(config.accessoryIds) || config.accessoryIds.some(value => !id(value))) fail('invalid accessories');
    const options = product.optionDefinitions;
    const optionAt = (position, optional = false) => {
      if (optional && position === 0) return null;
      if (!Number.isInteger(position) || position < 1 || position > 3) fail('invalid option position');
      const matches = options.filter(option => option.position === position);
      if (matches.length !== 1) fail('missing or ambiguous option role');
      return matches[0];
    };
    const size = optionAt(config.sizePosition, true), finish = optionAt(config.finishPosition, true);
    if (size && finish && size === finish) fail('duplicate option roles');
    if (config.personalization === 'print' && !finish) fail('print requires a design option');
    const refs = [['waterId', 'water_valves'], ['alternateWaterId', 'water_valves'], ['heaterId', 'heater'], ['hybridId', 'hybrid_valves']];
    const connections = data.connections;
    if (!Array.isArray(connections) || !unique(connections.map(item => item.id))) fail('duplicate connection products');
    if (!Array.isArray(data.accessories) || !unique(data.accessories.map(item => item.id))) fail('duplicate accessory products');
    const referenced = refs.filter(([key]) => config[key] != null).map(([key]) => config[key]);
    if (referenced.some(value => !id(value) || value === product.id) || !unique(referenced)) fail('invalid component references');
    if (connections.some(item => !referenced.includes(item.id))) fail('unexpected component');
    for (const [key, role] of refs) {
      if (config[key] == null) continue;
      const item = connections.find(item => item.id === config[key]);
      if (!item || item.componentRole !== role || !roles.includes(item.componentRole)) fail('component role mismatch');
      if (item.variantCount !== item.variants.length || !item.variants.length) fail('incomplete component variants');
      if (role === 'water_valves' && !['angled', 'double_angled'].includes(item.valveGeometry)) fail('missing valve geometry');
      if (key === 'alternateWaterId' && item.valveGeometry !== 'angled') fail('alternate water geometry');
      if (role === 'heater') {
        const keys = item.variants.map(variant => {
          const technical = variant.technical;
          if (!technical || !Number.isInteger(technical.power) || technical.power <= 0 || !['SPC', 'NOC'].includes(technical.cable) || !['WH', 'BL'].includes(technical.colour)) fail('incomplete heater variant');
          return `${technical.power}:${technical.cable}:${technical.colour}`;
        });
        if (!unique(keys)) fail('ambiguous heater variants');
      }
      if (role === 'hybrid_valves') {
        const colours = item.variants.map(variant => variant.technical?.colour);
        if (colours.some(colour => !['WH', 'BL', 'CH'].includes(colour)) || !unique(colours)) fail('ambiguous hybrid colours');
      }
    }
    const needs = (mode, keys) => { if (config.modes.includes(mode) && keys.some(key => !config[key])) fail(`missing ${mode} component`); };
    needs('water', ['waterId']); needs('water_angled', ['alternateWaterId']);
    needs('plug', ['heaterId']); needs('hidden', ['heaterId']); needs('hybrid', ['heaterId', 'hybridId']);
    if (config.modes.includes('water_angled')) {
      const primary = connections.find(item => item.id === config.waterId);
      if (primary?.valveGeometry !== 'double_angled') fail('alternate requires double-angled primary');
    }
    if (data.accessories.length !== config.accessoryIds.length || data.accessories.some(item => !config.accessoryIds.includes(item.id) || item.componentRole !== 'accessory' || item.variantCount !== item.variants.length || !item.variants.length)) fail('accessory compatibility mismatch');
    const baseIds = new Set(product.variants.map(variant => variant.id));
    const legacyBaseIds = Object.fromEntries(Object.entries(legacy?.legacyBaseIds || {}).filter(([, target]) => baseIds.has(target)));
    return {
      metadata: true,
      batch: legacy?.batch ?? true,
      title: data.modelTitle || product.title,
      accessoriesEnabled: config.accessoryIds.length > 0,
      accessoryIds: [...config.accessoryIds],
      waterId: config.waterId,
      alternateWaterId: config.alternateWaterId,
      heaterId: config.heaterId,
      hybridId: config.hybridId,
      waterDefaults: legacy?.waterDefaults,
      waterOnly: !config.modes.some(mode => ['plug', 'hidden', 'hybrid'].includes(mode)),
      connectionModes: [...config.modes],
      sizePosition: config.sizePosition,
      finishPosition: config.finishPosition,
      sizeValueIds: (size?.values || []).map(value => value.id),
      colourValueIds: (finish?.values || []).map(value => value.id),
      print: config.personalization === 'print',
      granite: config.personalization === 'slab',
      legacyBaseIds,
      inheritedPowers: inheritPowers(product, options, finish),
      powers: {},
      sizeDimensions: {}
    };
  }
  function requiredPower(profile, variant) {
    if (!profile?.metadata || profile.waterOnly) return null;
    const power = variant?.technical?.power;
    if (power != null) return validPower(power) ? power : null;
    const inherited = profile.inheritedPowers?.[variant?.id];
    return validPower(inherited) ? inherited : null;
  }
  window.TmxConfiguration = Object.freeze({resolve, requiredPower});
})();
